// QSM_VERSION_2B2 — multi-signer flow met sleep + per-vakje signer
import FormsTabs from '../components/FormsTabs';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as pdfjsLib from 'pdfjs-dist';
import api from '../services/api';

pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf-worker/pdf.worker.min.js';

const RENDER_SCALE = 1.5;
const MIN_W = 60;
const MIN_H = 24;

// === SNAP A+D: snap-to-grid + snap-to-field ===
const SNAP_GRID_PX = 8;       // vaste raster
const SNAP_THRESHOLD_PX = 6;  // afstand waarop vakje aan andere edge plakt
const snapAxis = (raw, edges) => {
  // field-snap heeft voorrang op grid
  for (const edge of edges) {
    if (Math.abs(raw - edge) <= SNAP_THRESHOLD_PX) return edge;
  }
  return Math.round(raw / SNAP_GRID_PX) * SNAP_GRID_PX;
};

// Pastel kleuren per signer (cycling)
const SIGNER_COLORS = [
  { border: '#3b82f6', bg: 'rgba(59,130,246,0.15)', label: '#1e40af' },   // blue
  { border: '#10b981', bg: 'rgba(16,185,129,0.15)', label: '#065f46' },   // green
  { border: '#f59e0b', bg: 'rgba(245,158,11,0.15)', label: '#92400e' },   // amber
  { border: '#ec4899', bg: 'rgba(236,72,153,0.15)', label: '#9d174d' },   // pink
  { border: '#8b5cf6', bg: 'rgba(139,92,246,0.15)', label: '#5b21b6' }    // purple
];

const QuickSignMulti = () => {
  const navigate = useNavigate();

  const [naam, setNaam] = useState('');
  const [file, setFile] = useState(null);
  const [pages, setPages] = useState([]);
  const [error, setError] = useState(null);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // items: [{ id, page, x_pdf, y_pdf, w_pdf, h_pdf, signer_naam, signer_email }]
  const [items, setItems] = useState([]);
  const [verstuurType, setVerstuurType] = useState('klant');
  const [signingStrength, setSigningStrength] = useState('basic');
  const [kopieNaarSigners, setKopieNaarSigners] = useState(true);
  const [tekenvolgorde, setTekenvolgorde] = useState('parallel'); // FORMS_V2
  const [geldigDagen, setGeldigDagen] = useState(30);
  const [klanten, setKlanten] = useState([]);
  const [personeel, setPersoneel] = useState([]);
  const [geselecteerdeKlant, setGeselecteerdeKlant] = useState(null);
  const [geselecteerdPersoneel, setGeselecteerdPersoneel] = useState(null);
  const [drawing, setDrawing] = useState(null); // { pageNum, startX, startY, currX, currY }
  // === DRAG_EXISTING_ITEM: bestaand vakje verplaatsen ===
  const [moving, setMoving] = useState(null); // { id, pageNum, offsetX, offsetY, w_px, h_px }
  const [pendingItem, setPendingItem] = useState(null); // item dat nog signer-popup moet krijgen
  // === DATE_FIELD_OPTION: na signer-info, sleep optioneel een datum-vakje ===
  const [pendingDateForItem, setPendingDateForItem] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch('/api/klanten', { headers: { 'Authorization': 'Bearer ' + token } })
      .then(r => r.ok ? r.json() : [])
      .then(d => setKlanten(Array.isArray(d) ? d : (d.data || d.klanten || [])))
      .catch(() => {});
    fetch('/api/gebruikers', { headers: { 'Authorization': 'Bearer ' + token } })
      .then(r => r.ok ? r.json() : [])
      .then(d => setPersoneel(Array.isArray(d) ? d : (d.data || d.gebruikers || [])))
      .catch(() => {});
  }, []); // item-id dat nog datum-vakje wacht

  const canvasRefs = useRef({});
  const overlayRefs = useRef({});

  // PDF laden
  const handleFile = useCallback(async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setError(null);
    setPages([]);
    setItems([]);
    canvasRefs.current = {};
    overlayRefs.current = {};

    if (!f.name.toLowerCase().endsWith('.pdf')) {
      setError('Alleen PDF-bestanden worden ondersteund.');
      setFile(null);
      return;
    }
    setFile(f);
    if (!naam) setNaam(f.name.replace(/\.pdf$/i, ''));

    setLoadingPdf(true);
    try {
      const buf = await f.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: buf }).promise;
      const info = [];
      for (let p = 1; p <= pdf.numPages; p++) {
        const page = await pdf.getPage(p);
        const viewport = page.getViewport({ scale: RENDER_SCALE });
        info.push({ pageNum: p, width: viewport.width, height: viewport.height, page, viewport });
      }
      setPages(info);
    } catch (err) {
      console.error('PDF laden mislukt:', err);
      setError('PDF kon niet worden geladen: ' + err.message);
    } finally {
      setLoadingPdf(false);
    }
  }, [naam]);

  // Render canvases
  useEffect(() => {
    pages.forEach(({ pageNum, page, viewport }) => {
      const canvas = canvasRefs.current[pageNum];
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      page.render({ canvasContext: ctx, viewport }).promise.catch(e =>
        console.error(`Pagina ${pageNum} render fout:`, e)
      );
    });
  }, [pages]);

  // Coordinate helpers
  const browserToPdf = (px, py, pw, ph, pageHeight) => ({
    x: px / RENDER_SCALE,
    y: (pageHeight - py - ph) / RENDER_SCALE,
    width: pw / RENDER_SCALE,
    height: ph / RENDER_SCALE
  });
  const pdfToBrowser = (xPdf, yPdf, wPdf, hPdf, pageHeight) => ({
    left:   xPdf * RENDER_SCALE,
    top:    pageHeight - (yPdf + hPdf) * RENDER_SCALE,
    width:  wPdf * RENDER_SCALE,
    height: hPdf * RENDER_SCALE
  });

  // SNAP-edges per pagina, in browser-space (links/rechts/midden, top/bottom/midden)
  const getSnapEdges = useCallback((pageNum) => {
    const pageInfo = pages.find(p => p.pageNum === pageNum);
    if (!pageInfo) return { x: [], y: [] };
    const xEdges = [];
    const yEdges = [];
    items.filter(i => i.page === pageNum).forEach(it => {
      const pos = pdfToBrowser(it.x_pdf, it.y_pdf, it.w_pdf, it.h_pdf, pageInfo.height);
      xEdges.push(pos.left, pos.left + pos.width, pos.left + pos.width / 2);
      yEdges.push(pos.top, pos.top + pos.height, pos.top + pos.height / 2);
    });
    return { x: xEdges, y: yEdges };
  }, [items, pages]);

  // Drawing handlers
  const onOverlayMouseDown = (pageNum, e) => {
    if (e.target !== e.currentTarget) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const rawX = e.clientX - rect.left;
    const rawY = e.clientY - rect.top;
    const edges = getSnapEdges(pageNum);
    const sx = snapAxis(rawX, edges.x);
    const sy = snapAxis(rawY, edges.y);
    setDrawing({ pageNum, startX: sx, startY: sy, currX: sx, currY: sy });
  };
  const onOverlayMouseMove = (e) => {
    // Verplaats bestaand vakje (signature OF date) — DATE_FIELD_DRAGGABLE
    if (moving) {
      const overlay = overlayRefs.current[moving.pageNum];
      if (!overlay) return;
      const rect = overlay.getBoundingClientRect();
      const rawX = e.clientX - rect.left - moving.offsetX;
      const rawY = e.clientY - rect.top - moving.offsetY;
      const boundX = Math.max(0, Math.min(rawX, rect.width - moving.w_px));
      const boundY = Math.max(0, Math.min(rawY, rect.height - moving.h_px));
      const allEdges = getSnapEdges(moving.pageNum);
      const ownPos = (() => {
        const it = items.find(i => i.id === moving.id);
        if (!it) return null;
        const pageInfo = pages.find(p => p.pageNum === moving.pageNum);
        if (!pageInfo) return null;
        if (moving.kind === 'date' && it.date_field) {
          return pdfToBrowser(it.date_field.x_pdf, it.date_field.y_pdf,
                              it.date_field.w_pdf, it.date_field.h_pdf, pageInfo.height);
        }
        if (moving.kind === 'name' && it.name_field) {
          return pdfToBrowser(it.name_field.x_pdf, it.name_field.y_pdf,
                              it.name_field.w_pdf, it.name_field.h_pdf, pageInfo.height);
        }
        return pdfToBrowser(it.x_pdf, it.y_pdf, it.w_pdf, it.h_pdf, pageInfo.height);
      })();
      const filterOwn = (edges, ownVals) => edges.filter(v => !ownVals.some(o => Math.abs(v - o) < 0.5));
      const ownX = ownPos ? [ownPos.left, ownPos.left + ownPos.width, ownPos.left + ownPos.width / 2] : [];
      const ownY = ownPos ? [ownPos.top, ownPos.top + ownPos.height, ownPos.top + ownPos.height / 2] : [];
      const sx = snapAxis(boundX, filterOwn(allEdges.x, ownX));
      const sy = snapAxis(boundY, filterOwn(allEdges.y, ownY));
      const pageInfo = pages.find(p => p.pageNum === moving.pageNum);
      if (!pageInfo) return;
      const pdfCoords = browserToPdf(sx, sy, moving.w_px, moving.h_px, pageInfo.height);
      setItems(prev => prev.map(it => {
        if (it.id !== moving.id) return it;
        if (moving.kind === 'date') {
          return { ...it, date_field: { ...it.date_field, x_pdf: pdfCoords.x, y_pdf: pdfCoords.y }};
        }
        if (moving.kind === 'name') {
          return { ...it, name_field: { ...it.name_field, x_pdf: pdfCoords.x, y_pdf: pdfCoords.y }};
        }
        return { ...it, x_pdf: pdfCoords.x, y_pdf: pdfCoords.y };
      }));
      return;
    }
    // Tekenen nieuw vakje
    if (!drawing) return;
    const overlay = overlayRefs.current[drawing.pageNum];
    if (!overlay) return;
    const rect = overlay.getBoundingClientRect();
    const rawX = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
    const rawY = Math.max(0, Math.min(e.clientY - rect.top, rect.height));
    const edges = getSnapEdges(drawing.pageNum);
    setDrawing(d => ({
      ...d,
      currX: snapAxis(rawX, edges.x),
      currY: snapAxis(rawY, edges.y)
    }));
  };
  const onOverlayMouseUp = () => {
    if (moving) { setMoving(null); return; }
    if (!drawing) return;
    const { pageNum, startX, startY, currX, currY } = drawing;
    let w = Math.abs(currX - startX);
    let h = Math.abs(currY - startY);
    if (w < MIN_W || h < MIN_H) { setDrawing(null); return; }

    // === MEDIAN_SIZE_LOCK: nieuwe vakjes krijgen mediaan-grootte van bestaande ===
    // Eerste vakje bepaalt de maat. Alle volgende vakjes worden even groot,
    // zodat 4 vakjes naast elkaar exact gelijk zijn.
    if (items.length > 0) {
      const median = (arr) => {
        const s = [...arr].sort((a, b) => a - b);
        const m = Math.floor(s.length / 2);
        return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
      };
      const widths_pdf = items.map(i => i.w_pdf);
      const heights_pdf = items.map(i => i.h_pdf);
      // Mediaan in PDF-coords -> terug naar browser-pixels
      w = median(widths_pdf) * RENDER_SCALE;
      h = median(heights_pdf) * RENDER_SCALE;
    }

    const x = Math.min(startX, currX);
    const y = Math.min(startY, currY);
    const pageInfo = pages.find(p => p.pageNum === pageNum);
    if (!pageInfo) { setDrawing(null); return; }
    const pdfCoords = browserToPdf(x, y, w, h, pageInfo.height);

    const newItem = {
      id: 'item_' + Date.now() + '_' + Math.random().toString(36).slice(2, 6),
      page: pageNum,
      x_pdf: pdfCoords.x,
      y_pdf: pdfCoords.y,
      w_pdf: pdfCoords.width,
      h_pdf: pdfCoords.height,
      signer_naam: '',
      signer_email: '',
      date_field: null
    };
    setPendingItem(newItem);
    setDrawing(null);
  };

  const savePendingItem = (naamVal, emailVal, wantDate, wantName) => {
    // === DATE_AUTO_PLACE: datum automatisch rechts naast handtekening ===
    let dateField = null;
    if (wantDate) {
      // EQUAL_BOX_SIZES: datum + naam beide 140x28
      const DATE_W_PDF = 140;
      const DATE_H_PDF = 28;
      const DATE_GAP_PDF = 8;
      const dx = pendingItem.x_pdf + pendingItem.w_pdf + DATE_GAP_PDF;
      const dy = pendingItem.y_pdf + (pendingItem.h_pdf - DATE_H_PDF) / 2;
      dateField = {
        page: pendingItem.page,
        x_pdf: dx, y_pdf: dy,
        w_pdf: DATE_W_PDF, h_pdf: DATE_H_PDF
      };
    }
    // === NAME_FIELD_FEATURE: naam automatisch boven handtekening ===
    let nameField = null;
    if (wantName) {
      const NAME_W_PDF = 140;
      const NAME_H_PDF = 28;
      const NAME_GAP_PDF = 4;
      // Boven handtekening, links uitgelijnd
      const nx = pendingItem.x_pdf;
      // PDF-coord stijgt naar boven, dus 'boven' = y + height + gap
      const ny = pendingItem.y_pdf + pendingItem.h_pdf + NAME_GAP_PDF;
      nameField = {
        page: pendingItem.page,
        x_pdf: nx, y_pdf: ny,
        w_pdf: NAME_W_PDF, h_pdf: NAME_H_PDF
      };
    }
    const newItem = { ...pendingItem, signer_naam: naamVal, signer_email: emailVal, date_field: dateField, name_field: nameField };
    setItems(prev => [...prev, newItem]);
    setPendingItem(null);
  };
  const cancelPending = () => setPendingItem(null);

  const removeItem = (id) => {
    if (!window.confirm('Vakje verwijderen?')) return;
    setItems(prev => prev.filter(i => i.id !== id));
  };

  const colorFor = (idx) => SIGNER_COLORS[idx % SIGNER_COLORS.length];

  // Versturen
  const canSubmit =
    naam.trim().length >= 2 &&
    file &&
    items.length >= 1 &&
    items.every(i => i.signer_naam.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(i.signer_email));

  const handleSubmit = async () => {
    if (!canSubmit) return;
    if (!window.confirm(`Versturen naar ${items.length} ondertekenaar(s)?`)) return;

    setSubmitting(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('bestand', file);
      fd.append('naam', naam.trim());
      // DATE_FIELD_PAYLOAD: stuur ook date_field mee als de signer er een heeft
      const swf = items.map(i => {
        const out = {
          naam: i.signer_naam.trim(),
          email: i.signer_email.trim(),
          signature_field: { x: i.x_pdf, y: i.y_pdf, width: i.w_pdf, height: i.h_pdf, page: i.page }
        };
        if (i.date_field) {
          out.date_field = {
            x: i.date_field.x_pdf,
            y: i.date_field.y_pdf,
            width: i.date_field.w_pdf,
            height: i.date_field.h_pdf,
            page: i.date_field.page
          };
        }
        if (i.name_field) {
          out.name_field = {
            x: i.name_field.x_pdf,
            y: i.name_field.y_pdf,
            width: i.name_field.w_pdf,
            height: i.name_field.h_pdf,
            page: i.name_field.page
          };
        }
        return out;
      });
      fd.append('signers_with_fields', JSON.stringify(swf));

      fd.append('signing_strength', signingStrength);
      fd.append('kopie_naar_signers', kopieNaarSigners ? 'true' : 'false');
      fd.append('tekenvolgorde', tekenvolgorde);
      fd.append('geldig_dagen', String(geldigDagen));
      if (verstuurType === 'klant' && geselecteerdeKlant && geselecteerdeKlant.id) fd.append('klant_id', geselecteerdeKlant.id);
      if (verstuurType === 'personeel' && geselecteerdPersoneel && geselecteerdPersoneel.id) fd.append('voor_gebruiker_id', geselecteerdPersoneel.id);
      const res = await api.post('/forms-workflow/quick-sign-multi', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (res.data?.success) {
        const ok = (res.data.mail_results || []).filter(m => m.success).length;
        const total = (res.data.mail_results || []).length;
        alert(`✅ Verstuurd: ${ok}/${total} mails succesvol`);
        navigate('/forms/inbox');
      } else {
        setError(res.data?.message || 'Onbekende fout');
      }
    } catch (err) {
      console.error('Versturen mislukt:', err);
      setError(err.response?.data?.message || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', padding: 24 }}>
      <div style={{ maxWidth: 1400, margin: '0 auto' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <div style={{ flex: 1 }}><FormsTabs active="nieuw" title="Nieuw document" /></div>
          <div style={{ marginLeft: 'auto', fontSize: 13, color: '#64748b' }}>
            {pages.length === 0 ? 'PDF kiezen' : `Sleep vakjes op de PDF — ${items.length} ondertekenaar${items.length === 1 ? '' : 's'}`}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '340px 1fr', gap: 16 }}>

          {/* Linker paneel */}
          <div>
            {/* SIGN_OPTIONS: niveau + kopie */}
            <div style={{ ...panel, marginBottom: 12 }}>
              <label style={lab}>Handtekening-niveau</label>
              <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                {[['basic', '✍ Gewoon', 'Tekenen via e-maillink'], ['aes', '🔐 Geavanceerd (AES)', 'SMS-code + tijdstempel derde partij']].map(([v, l, t]) => (
                  <button key={v} type="button" title={t} onClick={(e) => { e.preventDefault(); setSigningStrength(v); }}
                    style={{ flex: 1, padding: '6px 8px', fontSize: 12, border: '1px solid #cbd5e1', borderRadius: 4,
                      background: signingStrength === v ? '#3b82f6' : 'white', color: signingStrength === v ? 'white' : '#1e293b', cursor: 'pointer', fontWeight: 500 }}>
                    {l}
                  </button>
                ))}
              </div>
              <div style={{ fontSize: 11, color: '#64748b', marginBottom: 10 }}>
                {signingStrength === 'aes' ? 'Ondertekenaar moet een SMS-code invoeren; telefoonnummer verplicht.' : 'Ondertekenaar tekent direct via de link.'}
              </div>
              <label style={lab}>Tekenvolgorde</label>
              <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                {[['parallel', '⇶ Gelijktijdig', 'Iedereen krijgt direct een link'], ['sequentieel', '→ Een voor een', 'Volgende ondertekenaar krijgt pas een link als de vorige getekend heeft (volgorde = volgorde in de lijst)']].map(([v, l, t]) => (
                  <button key={v} type="button" title={t} onClick={(e) => { e.preventDefault(); setTekenvolgorde(v); }}
                    style={{ flex: 1, padding: '6px 8px', fontSize: 12, border: '1px solid #cbd5e1', borderRadius: 4,
                      background: tekenvolgorde === v ? '#3b82f6' : 'white', color: tekenvolgorde === v ? 'white' : '#1e293b', cursor: 'pointer', fontWeight: 500 }}>
                    {l}
                  </button>
                ))}
              </div>
              <label style={{ ...lab, display: 'flex', alignItems: 'center', gap: 8 }}>
                Geldig
                <select value={geldigDagen} onChange={(e) => setGeldigDagen(parseInt(e.target.value, 10))} style={{ padding: '4px 6px', fontSize: 12 }}>
                  {[7, 14, 30, 60, 90].map(d => <option key={d} value={d}>{d} dagen</option>)}
                </select>
                <span style={{ fontSize: 11, color: '#64748b', fontWeight: 400 }}>herinnering na 3 en 7 dagen, daarna verloopt het</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                <input type="checkbox" checked={kopieNaarSigners} onChange={(e) => setKopieNaarSigners(e.target.checked)} />
                Ondertekenaars krijgen na afronding de definitieve PDF gemaild
              </label>
            </div>
            <div style={panel}>
              <label style={lab}>
                Naam document <span style={{ color: '#dc2626' }}>*</span>
                <div style={{display:'flex',gap:6,marginBottom:8}}>
                  <button type="button" onClick={(e) => {e.preventDefault(); setVerstuurType('klant');}} style={{flex:1,padding:'6px 10px',fontSize:13,border:'1px solid #cbd5e1',borderRadius:4,background:verstuurType==='klant'?'#3b82f6':'white',color:verstuurType==='klant'?'white':'#1e293b',cursor:'pointer',fontWeight:500}}>📋 Klant</button>
                  <button type="button" onClick={(e) => {e.preventDefault(); setVerstuurType('personeel');}} style={{flex:1,padding:'6px 10px',fontSize:13,border:'1px solid #cbd5e1',borderRadius:4,background:verstuurType==='personeel'?'#3b82f6':'white',color:verstuurType==='personeel'?'white':'#1e293b',cursor:'pointer',fontWeight:500}}>👤 Personeel</button>
                </div>
                <select value={verstuurType === 'klant' ? (geselecteerdeKlant?.id || '') : (geselecteerdPersoneel?.id || '')} onChange={(e) => {
                  const id = parseInt(e.target.value);
                  if (verstuurType === 'klant') {
                    const k = klanten.find(x => x.id === id);
                    setGeselecteerdeKlant(k || null);
                    if (k) {
                      const naamFill = k.contactpersoon || k.naam || '';
                      const emailFill = k.email || '';
                      setItems(prev => prev.map(it => ({ ...it, signer_naam: naamFill, signer_email: emailFill })));
                    }
                  } else {
                    const m = personeel.find(x => x.id === id);
                    setGeselecteerdPersoneel(m || null);
                    if (m) {
                      const naamFill = ((m.voornaam || '') + ' ' + (m.achternaam || '')).trim();
                      const emailFill = m.email || '';
                      setItems(prev => prev.map(it => ({ ...it, signer_naam: naamFill, signer_email: emailFill })));
                    }
                  }
                }} style={{width:'100%',padding:8,border:'1px solid #cbd5e1',borderRadius:4,fontSize:14,marginBottom:8}}>
                  <option value="">— Geen {verstuurType === 'klant' ? 'klant' : 'personeel'} —</option>
                  {(verstuurType === 'klant' ? klanten : personeel).map(x => (
                    <option key={x.id} value={x.id}>
                      {verstuurType === 'klant' ? (x.naam + (x.email ? ' — ' + x.email : '')) : (((x.voornaam || '') + ' ' + (x.achternaam || '')).trim() + (x.email ? ' — ' + x.email : ''))}
                    </option>
                  ))}
                </select>
                <input type="text" value={naam} onChange={(e) => setNaam(e.target.value)}
                  placeholder="Bijv. Offerte Janssen" maxLength={120} style={inp} />
              </label>

              <label style={lab}>
                PDF-bestand <span style={{ color: '#dc2626' }}>*</span>
                {!file && (
                  <div style={dropArea}>
                    <input type="file" accept=".pdf,application/pdf" onChange={handleFile} style={fileInput} />
                    <div style={{ pointerEvents: 'none' }}>
                      <div style={{ fontSize: 32, marginBottom: 4 }}>📄</div>
                      <div style={{ fontWeight: 600, color: '#1e3a5f' }}>Klik om PDF te kiezen</div>
                    </div>
                  </div>
                )}
                {file && (
                  <div style={fileChip}>
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      📄 {file.name}
                    </span>
                    <button onClick={() => { setFile(null); setPages([]); setItems([]); }} style={btnLink}>×</button>
                  </div>
                )}
              </label>
            </div>

            {/* Items lijst */}
            <div style={{ ...panel, marginTop: 12 }}>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 10 }}>
                Ondertekenaars ({items.length})
              </div>
              {items.length === 0 && (
                <div style={{ fontSize: 13, color: '#94a3b8', fontStyle: 'italic' }}>
                  Nog geen vakjes. Sleep met je muis op de PDF om er één toe te voegen.
                </div>
              )}
              {items.map((it, idx) => {
                const c = colorFor(idx);
                return (
                  <div key={it.id} style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: 8, marginBottom: 6,
                    background: c.bg, border: `1px solid ${c.border}`, borderRadius: 6
                  }}>
                    <div style={{
                      width: 22, height: 22, borderRadius: 11,
                      background: c.border, color: 'white',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 12, fontWeight: 700, flexShrink: 0
                    }}>{idx + 1}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: c.label, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {it.signer_naam}
                      </div>
                      <div style={{ fontSize: 11, color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {it.signer_email} · pag. {it.page}
                      </div>
                    </div>
                    <button onClick={() => removeItem(it.id)} style={{
                      background: 'transparent', border: 'none', color: '#dc2626',
                      cursor: 'pointer', fontSize: 16, padding: 4
                    }} title="Verwijderen">🗑</button>
                  </div>
                );
              })}
            </div>

            {error && <div style={errorBox}>⚠️ {error}</div>}

            <button
              disabled={!canSubmit || submitting}
              onClick={handleSubmit}
              style={{
                ...btnPrimary, width: '100%', marginTop: 12,
                opacity: (!canSubmit || submitting) ? 0.4 : 1,
                cursor: (!canSubmit || submitting) ? 'not-allowed' : 'pointer'
              }}>
              {submitting ? 'Versturen…' : `📧 Verstuur naar ${items.length} ondertekenaar${items.length === 1 ? '' : 's'}`}
            </button>
            {!canSubmit && !submitting && (
              <small style={{ ...hint, display: 'block', marginTop: 6 }}>
                {!file && 'Kies een PDF. '}
                {file && naam.trim().length < 2 && 'Vul een naam in. '}
                {file && naam.trim().length >= 2 && items.length === 0 && 'Sleep minstens één vakje. '}
                {items.some(i => !i.signer_naam.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(i.signer_email)) && 'Vul alle signers correct in. '}
              </small>
            )}
          </div>

          {/* Rechter paneel: PDF + sleep-overlay */}
          <div style={panel}>
            {pages.length === 0 && !loadingPdf && (
              <div style={emptyState}>
                <div style={{ fontSize: 48, marginBottom: 12, opacity: 0.5 }}>📄</div>
                <div style={{ fontWeight: 600, color: '#475569' }}>Nog geen PDF</div>
                <small style={{ color: '#94a3b8' }}>Kies links een bestand om te beginnen.</small>
              </div>
            )}
            {loadingPdf && <div style={emptyState}>⏳ PDF aan het renderen…</div>}
            {pages.length > 0 && (
              <div style={{ overflow: 'auto', maxHeight: 'calc(100vh - 140px)' }}
                onMouseMove={onOverlayMouseMove} onMouseUp={onOverlayMouseUp}>
                <div style={{ marginBottom: 12, fontSize: 13, color: '#475569' }}>
                  {pages.length} pagina{pages.length !== 1 ? 's' : ''} · sleep met de muis voor een handtekening-vakje
                </div>
                {pages.map(({ pageNum, width, height }) => (
                  <div key={pageNum} style={{ marginBottom: 16 }}>
                    <div style={pageBadge}>Pagina {pageNum}</div>
                    <div style={{ position: 'relative', width, height, marginTop: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', borderRadius: 4, overflow: 'hidden' }}>
                      <canvas
                        ref={el => { canvasRefs.current[pageNum] = el; }}
                        width={width} height={height}
                        style={{ display: 'block' }}
                      />
                      <div
                        ref={el => { overlayRefs.current[pageNum] = el; }}
                        onMouseDown={(e) => onOverlayMouseDown(pageNum, e)}
                        style={{
                          position: 'absolute', inset: 0,
                          cursor: drawing ? 'crosshair' : 'crosshair'
                        }}
                      >
                        {/* Bestaande items op deze pagina */}
                        {/* Naam-vakjes (gekoppeld aan signer) */}
                        {items.filter(i => i.name_field && i.name_field.page === pageNum).map(it => {
                          const idx = items.findIndex(x => x.id === it.id);
                          const c = colorFor(idx);
                          const nf = it.name_field;
                          const npos = pdfToBrowser(nf.x_pdf, nf.y_pdf, nf.w_pdf, nf.h_pdf, height);
                          return (
                            <div key={it.id + '_name'}
                              onMouseDown={(ev) => {
                                ev.stopPropagation();
                                const overlay = overlayRefs.current[pageNum];
                                if (!overlay) return;
                                const rect = overlay.getBoundingClientRect();
                                setMoving({
                                  id: it.id,
                                  kind: 'name',
                                  pageNum,
                                  offsetX: (ev.clientX - rect.left) - npos.left,
                                  offsetY: (ev.clientY - rect.top) - npos.top,
                                  w_px: npos.width,
                                  h_px: npos.height
                                });
                              }}
                              style={{
                                position: 'absolute',
                                left: npos.left, top: npos.top, width: npos.width, height: npos.height,
                                background: c.bg, border: `2px dotted ${c.border}`, borderRadius: 4,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: 11, fontWeight: 600, color: c.label,
                                cursor: moving && moving.id === it.id && moving.kind === 'name' ? 'grabbing' : 'grab',
                                userSelect: 'none'
                              }}>
                              👤 {idx + 1}. {it.signer_naam}
                            </div>
                          );
                        })}
                        {/* Datum-vakjes (gekoppeld aan signer) */}
                        {items.filter(i => i.date_field && i.date_field.page === pageNum).map(it => {
                          const idx = items.findIndex(x => x.id === it.id);
                          const c = colorFor(idx);
                          const df = it.date_field;
                          const dpos = pdfToBrowser(df.x_pdf, df.y_pdf, df.w_pdf, df.h_pdf, height);
                          return (
                            <div key={it.id + '_date'}
                              onMouseDown={(e) => {
                                e.stopPropagation();
                                const overlay = overlayRefs.current[pageNum];
                                if (!overlay) return;
                                const rect = overlay.getBoundingClientRect();
                                setMoving({
                                  id: it.id,
                                  kind: 'date',
                                  pageNum,
                                  offsetX: (e.clientX - rect.left) - dpos.left,
                                  offsetY: (e.clientY - rect.top) - dpos.top,
                                  w_px: dpos.width,
                                  h_px: dpos.height
                                });
                              }}
                              style={{
                                position: 'absolute',
                                left: dpos.left, top: dpos.top, width: dpos.width, height: dpos.height,
                                background: c.bg, border: `2px dashed ${c.border}`, borderRadius: 4,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: 11, fontWeight: 600, color: c.label,
                                cursor: moving && moving.id === it.id && moving.kind === 'date' ? 'grabbing' : 'grab',
                                userSelect: 'none'
                              }}>
                              📅 {idx + 1}. datum
                            </div>
                          );
                        })}
                        {items.filter(i => i.page === pageNum).map(it => {
                          const idx = items.findIndex(x => x.id === it.id);
                          const c = colorFor(idx);
                          const pos = pdfToBrowser(it.x_pdf, it.y_pdf, it.w_pdf, it.h_pdf, height);
                          return (
                            <div key={it.id}
                              onMouseDown={(e) => {
                                e.stopPropagation();
                                const overlay = overlayRefs.current[pageNum];
                                if (!overlay) return;
                                const rect = overlay.getBoundingClientRect();
                                setMoving({
                                  id: it.id,
                                  kind: 'signature',
                                  pageNum,
                                  offsetX: (e.clientX - rect.left) - pos.left,
                                  offsetY: (e.clientY - rect.top) - pos.top,
                                  w_px: pos.width,
                                  h_px: pos.height
                                });
                              }}
                              style={{
                                position: 'absolute',
                                left: pos.left, top: pos.top, width: pos.width, height: pos.height,
                                background: c.bg, border: `2px solid ${c.border}`, borderRadius: 4,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: 12, fontWeight: 700, color: c.label,
                                cursor: moving && moving.id === it.id ? 'grabbing' : 'grab',
                                userSelect: 'none'
                              }}>
                              {idx + 1}. {it.signer_naam}
                            </div>
                          );
                        })}
                        {/* Drawing preview */}
                        {drawing && drawing.pageNum === pageNum && (
                          <div style={{
                            position: 'absolute',
                            left: Math.min(drawing.startX, drawing.currX),
                            top: Math.min(drawing.startY, drawing.currY),
                            width: Math.abs(drawing.currX - drawing.startX),
                            height: Math.abs(drawing.currY - drawing.startY),
                            background: 'rgba(30,58,95,0.15)', border: '2px dashed #1e3a5f', pointerEvents: 'none'
                          }} />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Pending-item modal: vraag signer-info */}
        {pendingItem && (
          <SignerModal
            initNaam={verstuurType === 'personeel'
              ? ((geselecteerdPersoneel?.voornaam || '') + ' ' + (geselecteerdPersoneel?.achternaam || '')).trim()
              : (geselecteerdeKlant?.contactpersoon || geselecteerdeKlant?.naam || '')}
            initEmail={verstuurType === 'personeel'
              ? (geselecteerdPersoneel?.email || '')
              : (geselecteerdeKlant?.email || '')}
            onSave={savePendingItem}
            onCancel={cancelPending}
          />
        )}
      </div>
    </div>
  );
};

// ===== Signer popup =====
const SignerModal = ({ onSave, onCancel, initNaam = '', initEmail = '' }) => {
  const [n, setN] = useState(initNaam);
  const [e, setE] = useState(initEmail);
  const [wantDate, setWantDate] = useState(false);
  const [wantName, setWantName] = useState(false);
  const valid = n.trim().length >= 2 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

  return (
    <div style={modalOverlay} onClick={onCancel}>
      <div style={modalBox} onClick={ev => ev.stopPropagation()}>
        <h3 style={{ margin: '0 0 8px', fontSize: 18 }}>Wie tekent hier?</h3>
        <p style={{ margin: '0 0 16px', fontSize: 13, color: '#64748b' }}>
          Naam komt op het vakje en in de mail die we versturen.
        </p>
        <label style={lab}>
          Naam <span style={{ color: '#dc2626' }}>*</span>
          <input autoFocus type="text" value={n} onChange={ev => setN(ev.target.value)}
            placeholder="Bijv. Jan Jansen" style={inp} />
        </label>
        <label style={lab}>
          E-mail <span style={{ color: '#dc2626' }}>*</span>
          <input type="email" value={e} onChange={ev => setE(ev.target.value)}
            placeholder="jan@voorbeeld.nl" style={inp} />
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, marginBottom: 4, fontSize: 13, cursor: 'pointer' }}>
          <input type="checkbox" checked={wantDate} onChange={ev => setWantDate(ev.target.checked)} />
          📅 Plak ook een datum-stempel naast de handtekening
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, marginBottom: 8, fontSize: 13, cursor: 'pointer' }}>
          <input type="checkbox" checked={wantName} onChange={ev => setWantName(ev.target.checked)} />
          👤 Plak ook een naam-vakje boven de handtekening
        </label>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button onClick={onCancel} style={btnSecondary}>Annuleren</button>
          <button onClick={() => valid && onSave(n.trim(), e.trim(), wantDate, wantName)}
            disabled={!valid}
            style={{ ...btnPrimary, opacity: valid ? 1 : 0.4, cursor: valid ? 'pointer' : 'not-allowed' }}>
            Toevoegen
          </button>
        </div>
      </div>
    </div>
  );
};

// ===== Styles =====
const panel = { background: 'white', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16 };
const lab = { display: 'block', marginBottom: 12, fontWeight: 600, fontSize: 13, color: '#1e293b' };
const inp = { display: 'block', width: '100%', marginTop: 6, padding: 10, border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' };
const hint = { fontSize: 12, color: '#94a3b8', fontWeight: 400 };
const btnSecondary = { padding: '8px 14px', border: '1px solid #cbd5e1', background: 'white', borderRadius: 6, fontSize: 14, cursor: 'pointer', fontWeight: 500 };
const btnPrimary = { padding: '12px 20px', border: 'none', background: '#1e3a5f', color: 'white', borderRadius: 8, fontSize: 14, cursor: 'pointer', fontWeight: 600 };
const btnLink = { background: 'none', border: 'none', color: '#dc2626', fontSize: 16, cursor: 'pointer', padding: 0, lineHeight: 1 };
const dropArea = { position: 'relative', marginTop: 6, padding: 20, border: '2px dashed #cbd5e1', borderRadius: 8, textAlign: 'center', background: '#f8fafc', cursor: 'pointer' };
const fileInput = { position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' };
const fileChip = { marginTop: 6, padding: '8px 12px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 };
const errorBox = { marginTop: 12, padding: 10, background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', borderRadius: 6, fontSize: 13 };
const emptyState = { textAlign: 'center', padding: 40, color: '#64748b', fontSize: 14 };
const pageBadge = { display: 'inline-block', padding: '3px 10px', background: '#1e3a5f', color: 'white', fontSize: 11, fontWeight: 600, borderRadius: 4 };
const modalOverlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 };
const modalBox = { background: 'white', borderRadius: 12, padding: 24, width: '100%', maxWidth: 460, boxShadow: '0 20px 50px rgba(0,0,0,0.3)' };

export default QuickSignMulti;
